import { useState, useEffect, useRef } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { QrCode, RefreshCw, Timer } from "lucide-react";

export default function QRAttendance({ jobId }) {
  const [qrData, setQrData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [countdown, setCountdown] = useState(20);
  const intervalRef = useRef(null);
  const countdownRef = useRef(null);

  const generate = async (isAutoRefresh = false) => {
    setLoading(true);
    try {
      const { data } = await api.post(`/jobs/${jobId}/qr/generate`, { auto_refresh: isAutoRefresh });
      setQrData(data);
      setCountdown(20);
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.message || "Failed to generate QR";
      toast.error(msg);
      console.error("QR Error:", e?.response?.status, e?.response?.data, e?.message);
    } finally {
      setLoading(false);
    }
  };

  // Auto refresh logic
  useEffect(() => {
    if (autoRefresh && qrData) {
      countdownRef.current = setInterval(() => {
        setCountdown(c => {
          if (c <= 1) {
            generate(true);
            return 20;
          }
          return c - 1;
        });
      }, 1000);
    } else {
      clearInterval(countdownRef.current);
    }
    return () => clearInterval(countdownRef.current);
  // eslint-disable-next-line
  }, [autoRefresh, qrData]);

  // Generate QR code image using a free API
  const qrImageUrl = qrData
    ? `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(qrData.checkin_url)}&bgcolor=0D0B14&color=ffffff&margin=10`
    : null;

  return (
    <div className="card-flat">
      <div className="flex items-center gap-2 mb-4">
        <QrCode className="w-5 h-5 text-brand" />
        <h3 className="font-display text-lg">QR Code Attendance</h3>
      </div>

      {!qrData ? (
        <div className="text-center py-6">
          <p className="text-sm text-muted-foreground mb-4">Generate a QR code for workers to scan and check in</p>
          <button onClick={() => generate(false)} disabled={loading} className="btn-primary">
            {loading ? "Generating…" : "Generate QR Code"}
          </button>
        </div>
      ) : (
        <div className="flex flex-col items-center gap-4">
          {/* QR Image */}
          <div className="p-3 bg-[#0D0B14] rounded-xl">
            <img
              src={qrImageUrl}
              alt="QR Code"
              className="w-48 h-48 rounded-lg"
            />
          </div>

          <p className="text-xs text-muted-foreground text-center">
            Workers scan this from their EarnMate account to check in
          </p>

          {/* Auto refresh toggle */}
          <div className="flex items-center gap-3 w-full">
            <label className="flex items-center gap-2 text-sm cursor-pointer flex-1">
              <div
                onClick={() => setAutoRefresh(a => !a)}
                className={`w-10 h-6 rounded-full transition relative cursor-pointer ${autoRefresh ? "bg-brand" : "bg-slate-300"}`}
              >
                <div className={`w-4 h-4 bg-white rounded-full absolute top-1 transition-all ${autoRefresh ? "left-5" : "left-1"}`} />
              </div>
              <span>Auto-refresh every 20s</span>
            </label>
            {autoRefresh && (
              <span className="flex items-center gap-1 text-xs text-brand font-mono">
                <Timer className="w-3 h-3" />{countdown}s
              </span>
            )}
          </div>

          {/* Manual refresh */}
          <div className="flex gap-2 w-full">
            <button onClick={() => generate(false)} disabled={loading} className="btn-outline flex-1 text-xs">
              <RefreshCw className="w-3.5 h-3.5" /> Refresh QR
            </button>
            <button
              onClick={() => {
                navigator.clipboard.writeText(qrData.checkin_url);
                toast.success("Check-in link copied!");
              }}
              className="btn-outline flex-1 text-xs"
            >
              Copy Link
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
